import {
  AnnouncementsService,
  AnnouncementsServiceModule,
} from '@b2b/common/components/announcements/announcements.service';
import { parseUrl, ParseUrlFilterModule } from '@b2b/common/filters';
import { ThemesData } from '@b2b/common/resolvers';
import { ApiService } from '@b2b/common/services/api/types';
import { AppsService, AppsServiceModule } from '@b2b/common/services/apps/apps.service';
import { AuthenticationService, AuthenticationServiceModule } from '@b2b/common/services/auth/authentication.service';
import { ContentHeaderServiceModule } from '@b2b/common/services/contentHeader';
import { NavigationService, NavigationServiceModule } from '@b2b/common/services/navigation/navigation.service';
import { SidebarService, SidebarServiceModule } from '@b2b/common/services/sidebar';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { ACMEResponse } from '@b2b/types/core';
import { TODO } from '@b2b/types/core/utils';
import { ACMEUserConfigResponse, UserData } from '@b2b/types/user';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IScope, ITimeoutService } from 'angular';

/**
 * DEV-13600: adds the "Gift Cards" backoffice nav item once the tenant's
 * active apps have loaded, if — and only if — the `stored_value_activate`
 * extendable is present (see AppsService.canActivateStoredValue()).
 *
 * No `permissions` array is checked against a specific RBAC permission —
 * permission-gating this feature is explicitly out of scope for now (the
 * plan's Decisions section, "Permission-enforcement mechanism"). That said,
 * omitting `permissions` entirely would NOT show the item unconditionally:
 * NavigationService.add() -> checkSecurity() -> checkPermissions() returns
 * `false` (hiding the item) when the nav's global security engine is
 * `'permissions'` (set app-wide via `navigationService.init(navigationConfig,
 * true, 'permissions')` in app.ts) and the item has no `permissions` array,
 * because the empty-array loop never sets `allow = true`. `permissions:
 * ['*']` is this codebase's own existing idiom for "show unconditionally
 * under the permissions engine" (see e.g. the `events.list` nav item and the
 * settings-view nav item in app.ts) — used here for that reason, not as a
 * new permission check.
 *
 * Extracted as a standalone, exported function (rather than inlined in
 * `ctrl.init()`) so it has direct unit-test coverage: this codebase's
 * AngularJS `$scope`-controller functions have no existing test harness
 * ($controller/angular-mocks isn't used anywhere in this repo), so keeping
 * the actual gating decision in a small pure function keeps it testable
 * without inventing one.
 */
export function addGiftCardsNavItemIfEnabled(appsService: AppsService, navigationService: NavigationService): void {
  if (!appsService.canActivateStoredValue()) {
    return;
  }

  navigationService.addAfter(
    {
      name: 'giftCards',
      title: 'Gift Cards',
      icon: 'giftCards',
      sref: 'application.giftCards',
      permissions: ['*'],
    },
    'orders',
    'default',
  );
}

function ApplicationController(
  $scope: IScope & { application: TODO },
  $state: StateService,
  $timeout: ITimeoutService,
  authenticationService: AuthenticationService,
  navigationService: NavigationService,
  sidebarService: SidebarService,
  contentHeaderService: TODO,
  helpersService: TODO,
  UserConfigData: { data: ACMEUserConfigResponse },
  userPreferencesService: TODO,
  ThemesData: { data: ACMEResponse<ThemesData> },
  themesHelperService: ThemesHelperService,
  apiService: ApiService,
  announcementsService: AnnouncementsService,
  appsService: AppsService
) {
  const ctrl = {
    view: 'full',
    user: {},

    overlayStatus: '',
    overlayStatusWide: false,

    overlayStatusStop: function (event) {
      $scope.$broadcast('OverlayStatus::stop');
    },

    init: function () {
      $scope.$on('Application::viewChange', ctrl.onApplicationViewChange);
      $scope.$on('Application::contentTitleChange', ctrl.onContentTitleChange);
      $scope.$on('Application::tenantSettingsUpdate', ctrl.onTenantSettingsUpdate);

      // DEV-13600: load the tenant's active apps and, once resolved, show the
      // "Gift Cards" nav item iff the stored_value_activate extendable is
      // present. No unsubscribe: ApplicationController lives for the whole
      // authenticated session (this file has no other rxjs subscription to
      // mirror cleanup from, and NavigationService.addAfter() is itself idempotent
      // — it no-ops on a name already present — so a lingering subscription
      // calling it again on a later state emission is harmless).
      appsService.ensureLoaded();
      appsService.state$.subscribe(function (state) {
        if (state.status === 'loaded') {
          addGiftCardsNavItemIfEnabled(appsService, navigationService);
        }
      });

      $scope.$on('Application::setOverlayStatus', function (e, status, options) {
        ctrl.overlayStatus = status;
        ctrl.overlayStatusWide = options?.wide || false;
      });

      $scope.$on('$locationChangeStart', function (event, next, prev) {
        if (/execution\/\d+/.test(prev)) {
          ctrl.overlayStatus = '';
          ctrl.overlayStatusWide = false;
        }
      });

      if (authenticationService.getUserFromCookie() != null) {
        // Main entry point of application should check if user has permissions
        // If a user is logged in but has no permissions we should log them out
        const userConfigData = UserConfigData.data?.success ? UserConfigData.data : null;
        const currentUser = userConfigData?.user ?? null;

        ctrl.checkPasswordStatus(currentUser);
        userPreferencesService.process(null, currentUser);

        if (ThemesData.data && ThemesData.data.success) {
          themesHelperService.resetModel();

          if (ThemesData && ThemesData.data.success) {
            ctrl.initThemes(ThemesData);
          }
        }
      } else {
        // User cookie has disappeared log user out
        $state.go('logout.default', {}, { notify: true });
      }
    },

    initThemes: function (response) {
      themesHelperService.setModel(response.data.payload);
      // Update main navigation
      $scope.$emit('Permissions::refresh');
    },

    checkPasswordStatus: function (currentUser: UserData | null) {
      if (authenticationService.isCSRImpersonating()) {
        return;
      }

      if (currentUser?.passwordStatus === 'EXPIRING_SOON') {
        const href = $state.href('application.account.profile', { changePassword: true });
        const userLink = parseUrl(href, 'Click here');

        announcementsService.addAnnouncement({
          type: 'warning',
          message: `Your password is about to expire! ${userLink} to change your password. ${currentUser.passwordDaysRemaining} days remaining.`,
        });
      }
    },

    isSidebarOpen: function () {
      return sidebarService.isShown;
    },

    onApplicationViewChange: function (e, data) {
      switch (data.view) {
        case 'full':
          ctrl.view = data.view;
          sidebarService.hide();
          break;
        default:
          ctrl.view = '';
      }
    },

    onContentTitleChange: function (e, data) {
      contentHeaderService.update(data);
    },

    onTenantSettingsUpdate: function (e, data) {
      apiService.themes.detail(true).then((response) => ctrl.initThemes(response));
    },
  };

  $scope.application = ctrl;
  ctrl.init();
}

ApplicationController.$inject = [
  '$scope',
  '$state',
  '$timeout',
  'AuthenticationService',
  'NavigationService',
  'SidebarService',
  'ContentHeaderService',
  'HelpersService',
  'UserConfigData',
  'UserPreferencesService',
  'ThemesData',
  'ThemesHelperService',
  'ApiService',
  'AnnouncementsService',
  'AppsService',
];

export const ApplicationControllerModule = angular
  .module('acme.view.application.controllers', [
    uiRouter,
    'acme.common.service.auth',
    'acme.common.service.permissions',
    AuthenticationServiceModule,
    NavigationServiceModule,
    SidebarServiceModule,
    ContentHeaderServiceModule,
    'acme.common.service.userPreferences',
    'acme.common.service.themesHelper',
    'acme.common.service.api',
    AnnouncementsServiceModule,
    AppsServiceModule,
    ParseUrlFilterModule,
  ])
  .controller('ApplicationController', ApplicationController).name;
